import { useQuery } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';
import { DEFENDER_ENABLED_PROJECT_CODES } from '../config/deviceDefender';

/**
 * Fetches paginated list of blocked devices for a project from Device Defender
 * @param {number} projectCode - Project code for multi-tenant isolation
 * @param {Object} params - Query parameters
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.pageSize=10] - Items per page
 * @param {string} [params.insertedOnFrom] - ISO date string, filter from (converted to epoch internally)
 * @param {string} [params.insertedOnTo] - ISO date string, filter to (converted to epoch internally)
 * @returns {Object} TanStack Query result with blocked devices data
 * @property {Object} data - Response with brand, blockedDevices array, pagination
 * @property {boolean} isLoading - True while initial fetch is in progress
 * @property {boolean} isFetching - True during any fetch (initial or refetch)
 * @property {Error} error - Error object if query failed
 * @property {Function} refetch - Function to refetch data
 * @property {boolean} isDefenderEnabled - Whether Device Defender is enabled for this project
 */
const useBlockedDevices = (projectCode, params = {}) => {
  const { page = 0, pageSize = 10, insertedOnFrom, insertedOnTo, enabled = true } = params;
  const numericProjectCode = Number(projectCode);
  const isDefenderEnabled = DEFENDER_ENABLED_PROJECT_CODES.includes(numericProjectCode);

  // Convert ISO date strings to epoch timestamps (SECONDS) as string - backend API expects seconds not milliseconds
  const fromEpoch = insertedOnFrom ? String(Math.floor(new Date(insertedOnFrom).getTime() / 1000)) : undefined;
  const toEpoch = insertedOnTo ? String(Math.floor(new Date(insertedOnTo).getTime() / 1000)) : undefined;

  const fetchBlockedDevices = async () => {
    try {
      const result = await GraphqlService.getBlockedDevices({
        projectId: projectCode,
        page,
        pageSize,
        insertedOnFrom: fromEpoch,
        insertedOnTo: toEpoch,
      });
      
      // Check if result contains error in various formats
      const errorStr = JSON.stringify(result);
      if (errorStr.includes('greater than the last page') || 
          result?.error?.includes('greater than the last page')) {
        return {
          brand: projectCode,
          blockedDevices: [],
          pagination: {
            pageNumber: 1,
            pageSize,
            totalPages: 0,
            totalItems: 0,
            itemsInPage: 0,
            isLast: true,
          },
        };
      }
      
      return result;
    } catch (error) {
      // Handle "page 1 is greater than last page 0" - means no blocked devices exist
      const errorStr = typeof error === 'string' ? error : JSON.stringify(error);
      if (errorStr.includes('greater than the last page') || 
          error?.message?.includes('greater than the last page')) {
        return {
          brand: projectCode,
          blockedDevices: [],
          pagination: {
            pageNumber: 1,
            pageSize,
            totalPages: 0,
            totalItems: 0,
            itemsInPage: 0,
            isLast: true,
          },
        };
      }
      throw error;
    }
  };

  const queryResult = useQuery(
    ['blockedDevices', projectCode, page, pageSize, fromEpoch, toEpoch],
    fetchBlockedDevices,
    {
      enabled: enabled && !!projectCode && isDefenderEnabled,
      staleTime: 60000, // 1 minute - list view can be slightly stale
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );

  return {
    ...queryResult,
    isDefenderEnabled,
  };
};

export { DEFENDER_ENABLED_PROJECT_CODES };
export default useBlockedDevices;
